import { useMemo } from 'react';
import { useField } from 'formik';
import { Box, Typography, Slider, FormHelperText } from '@mui/material';

import { useFormBusy } from './formBusyContext';
import { getMarkerOptions } from 'utils/v2/visaApplication';
import useTranslate from 'hooks/useTranslate';

type FormikRangeSliderProps = {
    name: string;
    label?: string;
    min: number;
    max: number;
    step?: number;
    marks?: Array<{ value: number; label: string }>;
    disabled?: boolean;
    /** Always show the thumb value by default (matches LanguageFormV2). */
    valueLabelDisplay?: 'on' | 'auto' | 'off';
    valueLabelFormat?: (value: number) => string | number;
};

export const FormikRangeSlider = ({
    name,
    label,
    min,
    max,
    step = 1,
    disabled = false,
    valueLabelDisplay = 'on',
    valueLabelFormat,
}: FormikRangeSliderProps) => {
    const { translate } = useTranslate();
    const formBusy = useFormBusy();
    const isDisabled = disabled || formBusy;
    const [field, meta, helpers] = useField(name);
    const marks = useMemo(() => getMarkerOptions({ min, max }), [min, max]);

    const handleChange = (_: Event, value: number | number[]) => {
        const next = Array.isArray(value) ? value[0] : value;
        // Validate against the new value, then mark touched without a stale re-validate.
        void helpers.setValue(next, true).then(() => {
            void helpers.setTouched(true, false);
        });
    };

    const displayValue = field.value !== undefined && field.value !== null ? field.value : min;

    return (
        <Box>
            {label && (
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    {translate(label)}
                </Typography>
            )}
            <Box sx={{ px: 2, pt: valueLabelDisplay === 'on' ? 1.5 : 0 }}>
                <Slider
                    value={displayValue}
                    onChange={handleChange}
                    onBlur={(e) => field.onBlur(e)}
                    min={min}
                    max={max}
                    step={step}
                    marks={marks}
                    disabled={isDisabled}
                    valueLabelDisplay={valueLabelDisplay}
                    valueLabelFormat={valueLabelFormat}
                    size="small"
                    sx={{
                        '& .MuiSlider-mark': {
                            display: 'none',
                        },
                        '& .MuiSlider-markLabel': {
                            fontSize: '0.75rem',
                        },
                        // Compact always-on value bubble
                        '& .MuiSlider-valueLabel': {
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            lineHeight: 1.2,
                            py: 0.25,
                            px: 0.5,
                            minWidth: 0,
                            borderRadius: 0.75,
                            top: -6,
                            '&:before': {
                                width: 8,
                                height: 8,
                            },
                            '& *': {
                                background: 'transparent',
                                color: 'inherit',
                            },
                        },
                        '& .MuiSlider-valueLabelLabel': {
                            px: 0,
                        },
                    }}
                />
            </Box>
            {meta.touched && meta.error && <FormHelperText error>{meta.error}</FormHelperText>}
        </Box>
    );
};
